import type { FC } from 'react';

import { Divider } from '../divider';
import { DropdownBody } from './dropdown-body';
import { DropdownButton } from './dropdown-button';
import { DropdownClose } from './dropdown-close';
import { DropdownContent } from './dropdown-content';
import { DropdownFooter } from './dropdown-footer';
import { DropdownHeader } from './dropdown-header';
import { DropdownHeading } from './dropdown-heading';
import { DropdownPortal } from './dropdown-portal';
import { DropdownRoot } from './dropdown-root';
import { DropdownTrigger } from './dropdown-trigger';
import type { DropdownPropsInterface } from './types/dropdown.types';

/**
 * Dropdown is a generic Floating UI component for contextual content. It is similar to the Popover
 * component, but it doesn't include an arrow nor a close button.
 *
 * Dropdown makes no assumption about the content. Make sure you set appropriate height and width
 * on your content. For a dropdown that focuses on a list of actions, see the Menu component.
 *
 * @since 7.28.0
 */

export const Dropdown: FC<DropdownPropsInterface> & {
  Root: typeof DropdownRoot;
  Trigger: typeof DropdownTrigger;
  Button: typeof DropdownButton;
  Portal: typeof DropdownPortal;
  Content: typeof DropdownContent;
  Header: typeof DropdownHeader;
  Heading: typeof DropdownHeading;
  Body: typeof DropdownBody;
  Footer: typeof DropdownFooter;
  Close: typeof DropdownClose;
} = (props: DropdownPropsInterface) => {
  const {
    children,
    trigger,
    header,
    body,
    footer,
    css: cssProp,
    dividers = false,
    style,
    className,
    portalId,
    portalRoot,
    portalPreserveTabOrder,
    ...rest
  } = props;
  return (
    <Dropdown.Root {...rest}>
      <Dropdown.Trigger>{trigger}</Dropdown.Trigger>
      <Dropdown.Portal id={portalId} root={portalRoot} preserveTabOrder={portalPreserveTabOrder}>
        <Dropdown.Content css={cssProp} style={style} className={className}>
          {header ? <Dropdown.Header>{header}</Dropdown.Header> : null}
          {header && dividers ? <Divider /> : null}
          {body ? <Dropdown.Body>{body}</Dropdown.Body> : null}
          {children}
          {footer && dividers ? <Divider /> : null}
          {footer ? <Dropdown.Footer>{footer}</Dropdown.Footer> : null}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

Dropdown.displayName = 'Dropdown';

Dropdown.Root = DropdownRoot;
Dropdown.Trigger = DropdownTrigger;
Dropdown.Button = DropdownButton;
Dropdown.Portal = DropdownPortal;
Dropdown.Content = DropdownContent;
Dropdown.Header = DropdownHeader;
Dropdown.Heading = DropdownHeading;
Dropdown.Body = DropdownBody;
Dropdown.Footer = DropdownFooter;
Dropdown.Close = DropdownClose;
